import chroma from 'chroma-js'
import React, { FC, useEffect, useMemo, useState } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { useAuth } from '../../../hooks/useAuth'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { DAW_COLORS, styled } from '../../../lib/stitches'
import { ArpMediaType } from '../../../types/common'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { BackButton, Box, Column, Row, Spacer, Text } from '../../core/Components'
import { Flex } from '../../core/Flex'
import { ArrowDownIcon } from '../../core/icons'
import { MediaTypes } from '../../core/Utils'
import { TopRightProfileCard } from '../../platform/dashboard/ProfileCard'
import Contributors from '../../platform/explore/common/Contributors'
import { PreviewPlayer } from '../../platform/explore/common/MediaPreview'
import { useAsyncEffect } from '../../platform/explore/common/utils'
import Gradient from '../../platform/Gradient'
import { useSampleInfo } from '../sample/SampleInfo'
import { useSongInfo } from '../song/SongInfo'
import { useStemInfo } from '../stem/StemInfo'
import MediaInfo, { MediaInfoButtons, MediaInfoColumn, MediaInfoTitle } from './MediaInfo'
import { PartnerProjects } from './partnerProjectUtils'
import useMetatagParser from './samplePackUtils'

export const SCROLL_TO_ID = 'dna-table'
const BACKUP_COLOR = DAW_COLORS.indigo03

const TopSectionContainer = styled(Row, {
  width: '100%',
  filter: 'drop-shadow(0px 4px 50px rgba(24, 74, 85, 0.06))',
  position: 'relative'
})

const MoreInfoText: FC = () => {
  const scrollToBelow = () => document.getElementById(SCROLL_TO_ID).scrollIntoView()

  return (
    <Row
      css={{
        textAlign: 'end',
        width: '100%',
        paddingRight: '50px',
        paddingBottom: '30px',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline'
        }
      }}
      onClick={scrollToBelow}
      verticalCenter
      justifyContentEnd
    >
      <Text
        css={{
          marginRight: '10px',
          marginBottom: '0px',
          fontSize: '12px'
        }}
      >
        See Usage Below
      </Text>
      <ArrowDownIcon style={{ height: '15px', width: '15px' }} />
    </Row>
  )
}

interface DetailPageTopSectionLayoutProps {
  columnLeft: React.ReactElement
  columnRight: React.ReactElement
  customImageSrc?: string
  color?: string
  showMoreInfo?: boolean
}

export const DetailPageTopSectionLayout: React.FC<DetailPageTopSectionLayoutProps> = ({
  columnLeft,
  columnRight,
  color,
  customImageSrc,
  showMoreInfo = true
}) => {
  const { user } = useAuth()
  const finalColor = color || '#F0F0F0'
  console.log('[finalColor]', finalColor)
  const palette = useMemo(() => {
    const scale = chroma.scale(['#F0F0F0', finalColor]).domain([0, 1])
    return [scale(0).hex(), scale(0.05).hex(), scale(0.4).hex(), scale(0.2).hex(), scale(0.1).hex()]
  }, [finalColor])
  useEffect(() => {
    console.log('[palette]', palette)
  }, palette)
  const background = customImageSrc ? `url(${customImageSrc})` : undefined
  return (
    <TopSectionContainer>
      <Gradient
        options={{ colorPalette: palette }}
        css={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          zIndex: -1
        }}
      />
      <Column
        css={{
          background,
          backgroundRepeat: 'repeat',
          width: '100%',
          paddingLeft: '15px',
          paddingRight: '15px',
          '@mobile': { paddingLeft: '30px', paddingRight: '30px' }
        }}
        justifySpaceBetween
      >
        {(user && (
          <Box css={{ '@tablet': { marginLeft: '30px' } }}>
            <BackButton
              style={{
                marginTop: '25px',
                marginBottom: '25px',
                '@tablet': { marginBottom: '60px' }
              }}
            />
          </Box>
        )) || (
          <Box css={{ width: '40px', height: '40px', marginTop: '25px', marginBottom: '60px' }} />
        )}
        <Row
          css={{
            marginBottom: '40px',
            flexDirection: 'column-reverse',
            '@tablet': { flexDirection: 'row' }
          }}
          horizontalCenter
          verticalCenter
        >
          <Spacer css={{ flex: '0.1', '@bigscreen': { flex: '0.5' } }} />
          <Box
            css={{
              flex: '1',
              maxWidth: '100%',
              minWidth: '100%',
              '@tablet': { maxWidth: '500px', minWidth: '400px' }
            }}
          >
            {columnLeft}
          </Box>
          <Spacer css={{ flex: '0.25', marginTop: '$32', '@tablet': { marginTop: 0 } }} />
          {columnRight}
          <Spacer css={{ flex: '0.1', '@bigscreen': { flex: '0.5' } }} />
        </Row>
        {showMoreInfo && <MoreInfoText />}
      </Column>
    </TopSectionContainer>
  )
}

interface DetailPageTopSectionProps {
  mediaType: string
  tokenID: number
}

const MobileSongDetails: React.FC<{ tokenID: number }> = ({ tokenID }) => {
  const { song, isReady, openseaLink } = useSongInfo({ tokenId: tokenID })

  return (
    <Flex direction="column" css={{ gap: '20px' }}>
      {isReady ? (
        <>
          <MediaInfoTitle
            title={song.metadata.title}
            artistEthAddress={song.user.walletAddress}
            artistName={song.user.displayName}
          />
          <MediaInfoColumn title="Contributors" flex="row" css={{ marginBottom: 0 }}>
            <Contributors
              index={tokenID}
              type={ArpMediaType.SONG}
              noText
              avatarSize="38px"
              maxContributors={6}
              css={{ marginTop: '0px', justifyContent: 'flex-end', transform: 'translateX(-7px)' }}
            />
          </MediaInfoColumn>
        </>
      ) : (
        <CenteredLoadingAnimation />
      )}
      <PreviewPlayer type="songs" index={tokenID} autoPlay={false} />
      {isReady ? (
        <MediaInfoButtons
          type={ArpMediaType.SONG}
          index={tokenID}
          openseaLink={openseaLink}
          css={{ marginTop: '16px' }}
        />
      ) : (
        <CenteredLoadingAnimation />
      )}
    </Flex>
  )
}

const MobileSampleDetails: React.FC<{ tokenID: number }> = ({ tokenID }) => {
  const { sample, isReady, openseaLink } = useSampleInfo({ tokenId: tokenID })

  return (
    <Flex direction="column" css={{ gap: '20px' }}>
      {isReady ? (
        <MediaInfoTitle
          title={sample.metadata.title}
          artistEthAddress={sample.user.walletAddress}
          artistName={sample.user.displayName}
        />
      ) : (
        <CenteredLoadingAnimation />
      )}
      <PreviewPlayer type="samples" index={tokenID} autoPlay={false} />
      {isReady ? (
        <MediaInfoButtons
          type={ArpMediaType.SAMPLE}
          index={tokenID}
          openseaLink={openseaLink}
          css={{ marginTop: '16px' }}
        />
      ) : (
        <CenteredLoadingAnimation />
      )}
    </Flex>
  )
}

const MobileStemDetails: React.FC<{ tokenID: number }> = ({ tokenID }) => {
  const { stem, isReady, openseaLink } = useStemInfo({ tokenId: tokenID })

  return (
    <Flex direction="column" css={{ gap: '20px' }}>
      {isReady ? (
        <>
          <MediaInfoTitle
            title={stem.metadata.title}
            artistEthAddress={stem.user.walletAddress}
            artistName={stem.user.displayName}
          />
          <MediaInfoColumn title="Contributors" flex="row" css={{ marginBottom: 0 }}>
            <Contributors
              index={tokenID}
              type={ArpMediaType.STEM}
              noText
              avatarSize="38px"
              maxContributors={6}
              css={{ marginTop: '0px', justifyContent: 'flex-end', transform: 'translateX(-7px)' }}
            />
          </MediaInfoColumn>
        </>
      ) : (
        <CenteredLoadingAnimation />
      )}
      <PreviewPlayer type="stems" index={tokenID} autoPlay={false} />
      {isReady ? (
        <MediaInfoButtons
          type={ArpMediaType.STEM}
          index={tokenID}
          openseaLink={openseaLink}
          css={{ marginTop: '16px' }}
        />
      ) : (
        <CenteredLoadingAnimation />
      )}
    </Flex>
  )
}

const mobileDetailsRenderSwitch = (mediaType: string, tokenID: number) => {
  switch (mediaType) {
    case MediaTypes.SONGS:
      return <MobileSongDetails tokenID={tokenID} />
    case MediaTypes.SAMPLES:
      return <MobileSampleDetails tokenID={tokenID} />
    case MediaTypes.STEMS:
      return <MobileStemDetails tokenID={tokenID} />
    default:
      throw new Error('Unknown media type')
  }
}

export const MobileDetails: React.FC<DetailPageTopSectionProps> = ({ mediaType, tokenID }) =>
  mobileDetailsRenderSwitch(mediaType, tokenID)

const DetailPageTopSection: FC<DetailPageTopSectionProps> = ({ mediaType, tokenID }) => {
  const [color, setColor] = useState<string>('#F0F0F0')
  const isLessThanTabletScreen = useMediaQuery(960)

  useAsyncEffect(async () => {
    const media = await ArpRegistry.getByIdAsync(tokenID)

    setColor(media.tokenColor || BACKUP_COLOR)
  }, [mediaType, tokenID])

  const { meta } = useMetatagParser(tokenID, mediaType)
  const customImageSrc = PartnerProjects[meta.project]
    ? PartnerProjects[meta.project].logoImagePath
    : undefined

  return (
    <>
      <DetailPageTopSectionLayout
        columnLeft={
          isLessThanTabletScreen ? (
            <MobileDetails mediaType={mediaType} tokenID={tokenID} />
          ) : (
            <PreviewPlayer type={mediaType} index={tokenID} autoPlay={false} />
          )
        }
        columnRight={
          !isLessThanTabletScreen && (
            <MediaInfo type={mediaType} index={tokenID} css={{ flex: '1', width: '100%' }} />
          )
        }
        color={color}
        customImageSrc={customImageSrc}
        showMoreInfo={!isLessThanTabletScreen}
      />
      <TopRightProfileCard />
    </>
  )
}

export default DetailPageTopSection
